import {
  createListEvent,
  Geotag,
  ListAddAttachmentPayload,
  PartialField,
  PatchField,
} from '@certhon/models';
import { useCallback, useMemo } from 'react';
import { useListContext } from './ListContext';

/**
 * Returns action functions scoped to a specific field node.
 *
 * Each action creates a ListEvent and appends it to the pending queue
 * via `addEvent` from ChecklistContext. Events are not posted to the
 * server until `commit()` is called.
 */
export function useFieldActions(fieldId: string) {
  const { user, addEvent, list } = useListContext();
  const listId = list.id;
  const userId = user.recnum;

  const patch = useCallback(
    (partial: PatchField) => {
      addEvent(
        createListEvent('PATCH', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, partialNode: partial },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const remove = useCallback(() => {
    addEvent(
      createListEvent('REMOVE', {
        list_id: listId,
        user_id: userId,
        payload: { targetNodeId: fieldId },
      }),
    );
  }, [addEvent, listId, userId, fieldId]);

  const insert = useCallback(
    (node: PartialField, index?: number) => {
      addEvent(
        createListEvent('INSERT', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, node, index },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const move = useCallback(
    (subjectNodeId: string, index?: number) => {
      addEvent(
        createListEvent('MOVE', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, subjectNodeId, index },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const addAttachment = useCallback(
    (attachment: ListAddAttachmentPayload['attachment']) => {
      addEvent(
        createListEvent('ADD_ATTACHMENT', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, attachment },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const removeAttachment = useCallback(
    (checksum: string) => {
      addEvent(
        createListEvent('REMOVE_ATTACHEMNT', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, checksum },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const addNote = useCallback(
    (text: string) => {
      addEvent(
        createListEvent('ADD_NOTE', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, note: text },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const editNote = useCallback(
    (noteId: string, text: string) => {
      addEvent(
        createListEvent('EDIT_NOTE', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, noteId, text },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const removeNote = useCallback(
    (noteId: string) => {
      addEvent(
        createListEvent('REMOVE_NOTE', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, noteId },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const addGeotag = useCallback(
    (geotag: Geotag) => {
      addEvent(
        createListEvent('ADD_GEOTAG', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, geotag },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const editGeotag = useCallback(
    (geotagId: string, geotag: Partial<Geotag>) => {
      addEvent(
        createListEvent('EDIT_GEOTAG', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, geotagId, geotag },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  const removeGeotag = useCallback(
    (geotagId: string) => {
      addEvent(
        createListEvent('REMOVE_GEOTAG', {
          list_id: listId,
          user_id: userId,
          payload: { targetNodeId: fieldId, geotagId },
        }),
      );
    },
    [addEvent, listId, userId, fieldId],
  );

  return useMemo(
    () => ({
      patch,
      remove,
      insert,
      move,
      addAttachment,
      removeAttachment,
      addNote,
      editNote,
      removeNote,
      addGeotag,
      editGeotag,
      removeGeotag,
    }),
    [
      patch,
      remove,
      insert,
      move,
      addAttachment,
      removeAttachment,
      addNote,
      editNote,
      removeNote,
      addGeotag,
      editGeotag,
      removeGeotag,
    ],
  );
}
